import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { usersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Save, Loader2, User, Mail, Phone, FileSignature } from 'lucide-react';

export default function MyProfilePage() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email_signature: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        phone: user.phone || '',
        email_signature: user.email_signature || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await usersAPI.updateMyProfile(form);
      toast.success('Profil mis à jour avec succès');
      if (refreshUser) {
        refreshUser();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const defaultSignature = `
Cordialement,

${user?.name || 'Votre nom'}
${user?.phone ? `Tél: ${user.phone}` : ''}
IDMAT OI - Vente et location de mini-pelles
La Réunion
`.trim();

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
          Mon Profil
        </h1>
        <p className="text-slate-500 mt-1">Gérez vos informations personnelles et votre signature email</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-yellow-500" />
              Informations personnelles
            </CardTitle>
            <CardDescription>
              Ces informations seront utilisées dans vos communications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nom complet</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jean Dupont"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="0693 XX XX XX"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Email (non modifiable)
              </Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-slate-50"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-yellow-500" />
              Signature Email
            </CardTitle>
            <CardDescription>
              Cette signature sera automatiquement ajoutée à la fin de vos emails envoyés depuis l'application
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email_signature">Votre signature personnalisée</Label>
              <Textarea
                id="email_signature"
                value={form.email_signature}
                onChange={(e) => setForm({ ...form, email_signature: e.target.value })}
                placeholder={defaultSignature}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-xs text-slate-500">
                Laissez vide pour utiliser la signature par défaut. Vous pouvez inclure votre nom, titre, téléphone, etc.
              </p>
            </div>

            {/* Preview */}
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
              <Label className="text-xs text-slate-500 uppercase tracking-wider">Aperçu de la signature</Label>
              <div className="mt-2 p-3 bg-white rounded border whitespace-pre-wrap text-sm">
                {form.email_signature || defaultSignature}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={loading} className="bg-yellow-500 hover:bg-yellow-600">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer mon profil
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
